import React, { useEffect } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { useParams, Navigate, Link, useNavigate } from 'react-router-dom';
import { Check, ArrowLeft, ArrowRight, Clock, Shield, Star, Phone, Home, TreePine, Wrench, Truck, FileText, Car, Building } from 'lucide-react';
import { Button } from './ui/button';
import { services } from '../mock';

// Icons mapping for services
const serviceIcons = {
  menage: Home,
  jardinage: TreePine,
  bricolage: Wrench,
  demenagement: Truck,
  administratif: FileText,
  vehicule: Car,
  airbnb: Building,
};

// Component to display other services
const OtherServicesSection = ({ currentServiceId }) => {
  const otherServices = services.filter(s => s.id !== currentServiceId);
  
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#153254] text-center mb-3">
          Découvrez nos autres services
        </h2>
        <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
          Nous proposons une gamme complète de services pour vous accompagner au quotidien
        </p>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {otherServices.map((service) => {
            const IconComponent = serviceIcons[service.id] || Home;
            return (
              <Link 
                key={service.id}
                to={`/service/${service.id}`}
                className="group bg-[#F4F7F6] hover:bg-[#0A2320] rounded-xl p-4 transition-all duration-300 text-center"
              >
                <div className="w-12 h-12 mx-auto mb-3 bg-white group-hover:bg-white/10 rounded-xl flex items-center justify-center transition-all">
                  <IconComponent className="h-6 w-6 text-[#153254] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-semibold text-sm text-[#153254] group-hover:text-white transition-colors">
                  {service.title}
                </h3>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Service details configuration - MISE À JOUR Janvier 2025
const serviceDetails = {
  menage: {
    name: 'Ménage à domicile',
    location: 'près de Méru',
    tagline: 'Un intérieur impeccable, sans effort',
    heroDescription: 'Confiez l\'entretien de votre maison à nos professionnels qualifiés. Nous prenons soin de votre intérieur comme s\'il s\'agissait du nôtre.',
    image: 'https://customer-assets.emergentagent.com/job_majord-booking/artifacts/clhxuxyv_smiling-housewife-apron-standing-kitchen.jpg',
    hasTaxCredit: true,
    sections: [
      {
        title: 'Entretien des pièces principales',
        description: 'Notre équipe assure l\'entretien complet de vos espaces de vie : salon, salle à manger, entrée. Nous utilisons des produits professionnels et des techniques adaptées à chaque surface pour un résultat impeccable.',
        image: 'https://images.unsplash.com/photo-1601002257790-ebe0966a85ae?w=800&q=80',
        items: ['Aspiration et lavage des sols', 'Dépoussiérage des meubles et surfaces', 'Nettoyage des vitres intérieures', 'Entretien des luminaires']
      },
      {
        title: 'Nettoyage des chambres',
        description: 'Un espace de repos mérite une attention particulière. Nous changeons les draps, aérons les pièces et veillons à ce que chaque recoin soit impeccable pour un sommeil réparateur.',
        image: 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800&q=80',
        items: ['Changement et pliage du linge de lit', 'Aspiration sous les meubles', 'Dépoussiérage des surfaces', 'Rangement soigné']
      },
      {
        title: 'Entretien des pièces humides',
        description: 'Salle de bain et WC nécessitent une hygiène irréprochable. Nous désinfectons, détartrons et faisons briller chaque élément pour garantir propreté et fraîcheur.',
        image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800&q=80',
        items: ['Désinfection des sanitaires', 'Détartrage robinetterie', 'Nettoyage des miroirs', 'Entretien des joints']
      },
      {
        title: 'Entretien de la cuisine',
        description: 'La cuisine est le cœur de la maison. Nous nettoyons en profondeur plans de travail, électroménager et sols pour un espace de préparation sain et agréable.',
        image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=80',
        items: ['Nettoyage des plans de travail', 'Entretien de l\'électroménager', 'Dégraissage des surfaces', 'Nettoyage de l\'évier']
      }
    ],
    complementary: [
      { title: 'Nettoyage en profondeur', description: 'Nettoyage approfondi de vos canapés, tapis et chaises pour un intérieur comme neuf.' },
      { title: 'Nettoyage des vitres', description: 'Vitres intérieures et extérieures accessibles pour une clarté parfaite.' }
    ],
    pricingTable: {
      headers: ['Formule', 'Tarif horaire', 'Après crédit d\'impôt'],
      rows: [
        ['Intervention ponctuelle', 'À partir de 30€/h', '15€/h'],
        ['Intervention régulière', '25€/h', '12,50€/h']
      ]
    },
    pricingNote: 'Réservation minimum 2 heures'
  },
  jardinage: {
    name: 'Jardinage à domicile',
    location: 'près de Méru',
    tagline: 'Un jardin soigné toute l\'année',
    heroDescription: 'Nos jardiniers expérimentés prennent soin de vos espaces verts pour vous offrir un extérieur agréable en toute saison.',
    image: 'https://customer-assets.emergentagent.com/job_majordhome-care/artifacts/0jpn0feu_joyeux-jardinier-plantant-des-semis.jpg',
    hasTaxCredit: true,
    sections: [
      {
        title: 'Tonte de pelouse',
        description: 'Une pelouse bien entretenue est la base d\'un jardin élégant. Nous adaptons la hauteur de coupe selon la saison et vos préférences pour un gazon dense et verdoyant.',
        image: 'https://images.unsplash.com/photo-1558904541-efa843a96f01?w=800&q=80',
        items: ['Tonte régulière adaptée', 'Ramassage de l\'herbe', 'Finitions aux bordures', 'Conseils d\'entretien']
      },
      {
        title: 'Désherbage et entretien des massifs',
        description: 'Le désherbage régulier permet à vos plantes de s\'épanouir. Nous prenons soin de vos massifs pour un jardin propre et organisé.',
        image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&q=80',
        items: ['Désherbage manuel soigneux', 'Binage des massifs', 'Paillage si nécessaire', 'Entretien des bordures']
      },
      {
        title: 'Taille des haies et arbustes',
        description: 'Une haie bien taillée structure votre jardin et préserve votre intimité. Nos jardiniers maîtrisent les techniques de taille adaptées à chaque espèce.',
        image: 'https://images.unsplash.com/photo-1734079692079-172d8243ebd3?w=800&q=80',
        items: ['Taille de formation', 'Taille d\'entretien', 'Évacuation des déchets', 'Respect des périodes']
      },
      {
        title: 'Ramassage des feuilles',
        description: 'En automne, les feuilles mortes s\'accumulent. Nous les ramassons et les évacuons pour garder votre jardin propre et éviter les nuisibles.',
        image: 'https://images.unsplash.com/photo-1634081727680-fa43e3237d5a?w=800&q=80',
        items: ['Ramassage complet', 'Nettoyage des allées', 'Dégagement des gouttières', 'Évacuation des déchets']
      }
    ],
    complementary: [
      { title: 'Plantation', description: 'Mise en place de vos plantes, arbustes et fleurs selon vos envies.' },
      { title: 'Arrosage', description: 'Arrosage régulier pendant vos absences ou en période de canicule.' },
      { title: 'Petit élagage', description: 'Taille des branches basses et entretien des petits arbres.' }
    ],
    pricing: 'Devis personnalisé selon superficie'
  },
  bricolage: {
    name: 'Travaux de bricolage',
    location: 'près de Méru',
    tagline: 'Les petits travaux en toute simplicité',
    heroDescription: 'Montage de meubles, fixations, petites réparations... Nos bricoleurs s\'occupent de tous vos petits travaux du quotidien.',
    image: 'https://images.unsplash.com/photo-1581783898377-1c85bf937427?w=1200&q=80',
    hasTaxCredit: true,
    sections: [
      {
        title: 'Montage de meubles',
        description: 'Vous avez reçu un meuble en kit ? Nos bricoleurs le montent avec précision et savoir-faire pour une installation solide et parfaitement alignée.',
        image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
        items: ['Montage tous types de meubles', 'Fixation au mur si nécessaire', 'Vérification de la stabilité', 'Évacuation des emballages']
      },
      {
        title: 'Pose de tringles à rideaux',
        description: 'Pour des rideaux qui tombent parfaitement, nous installons vos tringles avec précision en tenant compte de vos fenêtres et de vos souhaits.',
        image: 'https://images.unsplash.com/photo-1745242395967-c69b5af856a6?w=800&q=80',
        items: ['Prise de mesures', 'Perçage et fixation', 'Installation solide', 'Résultat discret et net']
      },
      {
        title: 'Fixation d\'étagères et placards',
        description: 'Étagères, tablettes, placards suspendus... Nous fixons vos rangements de façon sécurisée, adaptée au type de mur et au poids supporté.',
        image: 'https://images.unsplash.com/photo-1595428774223-ef52624120d2?w=800&q=80',
        items: ['Étude du support mural', 'Choix des fixations adaptées', 'Pose de niveau', 'Finitions soignées']
      },
      {
        title: 'Petites réparations',
        description: 'Poignée de porte cassée, robinet qui fuit, joint à refaire... Nous intervenons rapidement pour les petits dépannages du quotidien.',
        image: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=800&q=80',
        items: ['Diagnostic du problème', 'Réparation ou remplacement', 'Test de fonctionnement', 'Conseils d\'entretien']
      }
    ],
    complementary: [
      { title: 'Peinture', description: 'Travaux de peinture sur devis.' },
      { title: 'Installation électroménager', description: 'Branchement et mise en service de vos appareils.' },
      { title: 'Accrochage de tableaux', description: 'Installation soignée de vos cadres et décorations murales.' }
    ],
    pricingTable: {
      headers: ['Prestation', 'Tarif', 'Après crédit d\'impôt'],
      rows: [
        ['Main d\'œuvre', 'À partir de 30€/h', '15€/h']
      ]
    },
    pricingNote: 'Prix hors fournitures. Besoin d\'aide ? Nous achetons le matériel nécessaire pour vous avant notre intervention.'
  },
  demenagement: {
    name: 'Aide au déménagement',
    location: 'près de Méru',
    tagline: 'Votre déménagement en toute sérénité',
    heroDescription: 'Nous vous aidons dans la préparation et le chargement de vos cartons pour un déménagement sans stress.',
    image: 'https://images.unsplash.com/photo-1600518464441-9154a4dea21b?w=1200&q=80',
    hasTaxCredit: false,
    sections: [
      {
        title: 'Aide cartons',
        description: 'Nous vous aidons à emballer vos affaires avec soin et méthode. Chaque carton est étiqueté pour faciliter le déballage.',
        image: 'https://images.unsplash.com/photo-1700165644892-3dd6b67b25bc?w=800&q=80',
        items: ['Emballage soigneux', 'Protection des objets fragiles', 'Étiquetage clair', 'Organisation par pièce']
      },
      {
        title: 'Aide chargement/déchargement',
        description: 'Nous portons vos cartons jusqu\'au véhicule et les déchargeons à destination avec précaution.',
        image: 'https://images.pexels.com/photos/4487512/pexels-photo-4487512.jpeg?w=800',
        items: ['Port des cartons', 'Chargement optimisé', 'Déchargement avec soin', 'Placement selon vos indications']
      }
    ],
    complementary: [
      { title: 'Déballage', description: 'Aide au déballage et rangement dans votre nouveau logement.' }
    ],
    pricing: 'À partir de 35€/heure',
    pricingNote: 'Nos tarifs n\'incluent pas le transport de meubles lourds (pianos, coffres, etc.). Le prix final sera ajusté selon le volume et la distance kilométrique entre vos deux adresses.'
  },
  administratif: {
    name: 'Aide administrative',
    location: 'près de Méru',
    tagline: 'Vos papiers en ordre, l\'esprit tranquille',
    heroDescription: 'Gestion de vos papiers, démarches en ligne, classement... Nous vous aidons à y voir plus clair dans votre administratif.',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&q=80',
    hasTaxCredit: true,
    sections: [
      {
        title: 'Tri et classement des documents',
        description: 'Nous organisons vos documents de manière logique et accessible. Fini le stress de chercher un papier important !',
        image: 'https://images.unsplash.com/photo-1568667256549-094345857637?w=800&q=80',
        items: ['Tri des documents', 'Classement thématique', 'Création de dossiers', 'Archivage organisé']
      },
      {
        title: 'Aide aux démarches en ligne',
        description: 'Nous vous accompagnons sur les sites administratifs : impôts, CAF, CPAM, retraite...',
        image: 'https://images.unsplash.com/photo-1666101043112-01000e1ff6c5?w=800&q=80',
        items: ['Navigation sur les sites', 'Remplissage de formulaires', 'Suivi des dossiers', 'Téléchargement de documents']
      }
    ],
    complementary: [
      { title: 'Rédaction de courriers', description: 'Aide à la rédaction de lettres administratives.' },
      { title: 'Imprimerie', description: 'Impression de vos documents et formulaires.' }
    ],
    pricingTable: {
      headers: ['Prestation', 'Tarif', 'Après crédit d\'impôt'],
      rows: [
        ['Aide administrative', 'À partir de 25€/h', '12,50€/h']
      ]
    }
  },
  vehicule: {
    name: 'Nettoyage de véhicule',
    location: 'près de Méru',
    tagline: 'Votre véhicule comme neuf',
    heroDescription: 'Nettoyage intérieur et/ou extérieur de votre véhicule, directement chez vous ou à déposer à l\'agence.',
    image: 'https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?w=1200&q=80',
    hasTaxCredit: false,
    sections: [
      {
        title: 'Nettoyage intérieur complet',
        description: 'Nous redonnons vie à l\'intérieur de votre véhicule : aspiration, nettoyage des plastiques, des tissus et des vitres pour un habitacle impeccable.',
        image: 'https://images.unsplash.com/photo-1714225417142-dbdad535071b?w=800&q=80',
        items: ['Aspiration complète', 'Nettoyage des plastiques', 'Entretien des tissus/cuirs', 'Vitres intérieures impeccables']
      },
      {
        title: 'Nettoyage extérieur complet',
        description: 'Carrosserie, jantes, vitres... nous faisons briller votre véhicule de l\'extérieur pour un rendu comme neuf.',
        image: 'https://images.unsplash.com/photo-1694678505383-676d78ea3b96?w=800&q=80',
        items: ['Lavage carrosserie', 'Nettoyage des jantes', 'Vitres extérieures', 'Séchage soigné']
      }
    ],
    complementary: [
      { title: 'Désodorisation', description: 'Élimination des mauvaises odeurs pour un habitacle frais. Sur devis.' },
      { title: 'Traitement cuir', description: 'Nettoyage et nourrissage de vos sièges en cuir. Sur devis.' }
    ],
    pricingTable: {
      headers: ['Type de véhicule', 'Intérieur', 'Extérieur', 'Complet'],
      rows: [
        ['Petit véhicule (mini, 2 places)', '60€', '40€', '100€'],
        ['Véhicule standard', '70€', '50€', '120€'],
        ['Gros véhicule (4x4, 7 places)', '90€', '60€', '150€']
      ]
    },
    pricingNote: 'Intervention à domicile ou à déposer à l\'agence'
  },
  airbnb: {
    name: 'Gestion Airbnb',
    location: 'près de Méru',
    tagline: 'Votre location courte durée clé en main',
    heroDescription: 'Confiez-nous la gestion complète de votre bien en location saisonnière : ménage, check-in/out, linge, jardinage...',
    image: 'https://images.unsplash.com/photo-1544984243-ec57ea16fe25?w=1200&q=80',
    hasTaxCredit: false,
    sections: [
      {
        title: 'Ménage entre deux locations',
        description: 'Nous préparons votre logement pour chaque nouveau voyageur : ménage complet, draps changés, serviettes fraîches.',
        image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&q=80',
        items: ['Nettoyage complet', 'Changement des draps', 'Serviettes propres', 'Vérification générale']
      },
      {
        title: 'Check-in / Check-out',
        description: 'Nous accueillons vos locataires, leur remettons les clés et leur présentons le logement.',
        image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80',
        items: ['Remise des clés', 'Présentation du logement', 'Conseils locaux', 'État des lieux']
      }
    ],
    complementary: [
      { title: 'Gestion du linge', description: 'Lavage, séchage et pliage de tout le linge de maison pour vos locations.' },
      { title: 'Courses', description: 'Approvisionnement en produits de base ou personnalisés pour vos voyageurs.' },
      { title: 'Jardinage', description: 'Entretien des espaces extérieurs de vos locations pour une première impression parfaite.' }
    ],
    pricing: 'Devis personnalisé selon vos besoins'
  }
};

// Floating Back Button Component - AMÉLIORÉ avec meilleur contraste et position
const FloatingBackButton = () => {
  const navigate = useNavigate();
  
  return (
    <button
      onClick={() => navigate('/')}
      className="fixed bottom-8 left-8 z-50 w-14 h-14 bg-[#153254] text-white rounded-full flex items-center justify-center shadow-[0_4px_20px_rgba(21,50,84,0.4)] hover:bg-[#122b26] hover:shadow-[0_6px_24px_rgba(21,50,84,0.5)] transition-all duration-200 hover:-translate-y-1"
      aria-label="Retour à l'accueil"
    >
      <ArrowLeft className="h-6 w-6" />
    </button>
  );
};

export const ServicePage = () => {
  const { serviceId } = useParams();
  const config = serviceDetails[serviceId];
  const serviceData = services.find(s => s.id === serviceId);
  
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);
  
  if (!config) {
    return <Navigate to="/" replace />;
  }
  
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section - Style premium */}
      <section className="relative min-h-[500px] md:min-h-[550px] flex items-center">
        <div className="absolute inset-0">
          <img 
            src={config.image} 
            alt={config.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#153254]/95 via-[#153254]/80 to-[#122b26]/70"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl py-24">
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2 leading-tight">
              {config.name}
            </h1>
            <p className="text-xl md:text-2xl text-white/80 mb-6 font-light">
              {config.location}
            </p>
            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl leading-relaxed">
              {config.heroDescription}
            </p>
            
            <div className="flex flex-wrap gap-4 items-center">
              <Link to={`/devis-premium?service=${serviceId}`}>
                <Button size="lg" className="bg-[#D4AF37] hover:bg-[#c9a030] text-white font-semibold px-8 py-6 text-lg shadow-lg">
                  Demandez un devis
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              
              {config.hasTaxCredit && (
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-5 py-3 rounded-lg">
                  <Check className="h-5 w-5 text-[#D4AF37]" />
                  <span className="text-white font-medium">Éligible crédit d'impôt 50%</span>
                  <button 
                    onClick={() => document.getElementById('pricing-section').scrollIntoView({ behavior: 'smooth' })}
                    className="ml-2 px-3 py-1.5 border border-white/40 text-white text-sm font-medium rounded-lg hover:bg-white/10 transition-colors"
                  >
                    Voir tarif
                  </button>
                </div>
              )}
              
              {!config.hasTaxCredit && (
                <button 
                  onClick={() => document.getElementById('pricing-section').scrollIntoView({ behavior: 'smooth' })}
                  className="px-4 py-3 border border-white/40 text-white font-medium rounded-lg hover:bg-white/10 transition-colors"
                >
                  Voir tarif
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
      
      {/* Service Sections - Layout alternating */}
      {config.sections.map((section, index) => (
        <section 
          key={index} 
          className={`py-20 ${index % 2 === 0 ? 'bg-white' : 'bg-[#F4F7F6]'}`}
        >
          <div className="container mx-auto px-4">
            <div className={`max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
              {/* Image */}
              <div className={`${index % 2 === 1 ? 'md:order-2' : ''}`}>
                <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src={section.image} 
                    alt={section.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              {/* Content */}
              <div className={`${index % 2 === 1 ? 'md:order-1' : ''}`}>
                <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#153254] mb-4">
                  {section.title}
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed mb-6">
                  {section.description}
                </p>
                
                <div className="space-y-3">
                  {section.items.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
                
                <Link to={`/devis-premium?service=${serviceId}`} className="inline-block mt-8">
                  <Button className="bg-[#153254] hover:bg-[#153254]/90 text-white font-medium">
                    Demandez un devis
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      ))}
      
      {/* Complementary Services */}
      {config.complementary && config.complementary.length > 0 && (
        <section className="py-20 bg-[#153254]">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-white mb-4 text-center">
                Services complémentaires
              </h2>
              <p className="text-white/70 text-center mb-12 text-lg">
                Pour compléter notre offre selon vos besoins
              </p>
              
              <div className="grid md:grid-cols-3 gap-6">
                {config.complementary.map((item, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/15 transition-colors">
                    <h3 className="font-semibold text-white text-lg mb-2">{item.title}</h3>
                    <p className="text-white/70 text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
      
      {/* Notice if exists */}
      {config.notice && (
        <section className="py-8 bg-amber-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <p className="text-amber-800 text-center">{config.notice}</p>
            </div>
          </div>
        </section>
      )}
      
      {/* Guarantees */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#153254] mb-12 text-center">
              Nos garanties
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-[#153254] rounded-xl flex items-center justify-center mx-auto mb-5">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-['Playfair_Display'] font-semibold text-xl text-[#153254] mb-3">Intervenants vérifiés</h3>
                <p className="text-gray-600">Tous nos professionnels sont sélectionnés et formés avec soin</p>
              </div>
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-[#153254] rounded-xl flex items-center justify-center mx-auto mb-5">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-['Playfair_Display'] font-semibold text-xl text-[#153254] mb-3">Réponse sous 24h</h3>
                <p className="text-gray-600">Votre devis personnalisé envoyé rapidement</p>
              </div>
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-[#153254] rounded-xl flex items-center justify-center mx-auto mb-5">
                  <Star className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-['Playfair_Display'] font-semibold text-xl text-[#153254] mb-3">Satisfaction garantie</h3>
                <p className="text-gray-600">Votre satisfaction est notre priorité absolue</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Découvrez nos autres services */}
      <OtherServicesSection currentServiceId={serviceId} />
      
      {/* Pricing & CTA */}
      <section id="pricing-section" className="py-20 bg-[#F4F7F6] scroll-mt-20">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#153254] mb-6">
              Planifiez votre {config.name.toLowerCase()}
            </h2>
            
            {/* Affichage tableau si pricingTable existe */}
            {config.pricingTable ? (
              <div className="bg-white rounded-xl p-4 md:p-6 shadow-md mb-4 overflow-x-auto">
                <p className="font-semibold text-[#153254] mb-4 text-lg">Nos tarifs</p>
                <table className="w-full min-w-[500px]">
                  <thead>
                    <tr className="border-b-2 border-[#D4AF37]">
                      {config.pricingTable.headers.map((header, i) => (
                        <th 
                          key={i} 
                          className={`py-3 px-2 text-sm md:text-base font-semibold ${
                            i === 0 ? 'text-left text-[#153254]' : 'text-center text-[#0A2320]'
                          }`}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {config.pricingTable.rows.map((row, rowIndex) => (
                      <tr key={rowIndex} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        {row.map((cell, cellIndex) => (
                          <td 
                            key={cellIndex}
                            className={`py-3 px-2 text-sm md:text-base ${
                              cellIndex === 0 
                                ? 'text-left text-gray-700 font-medium' 
                                : 'text-center font-semibold text-[#153254]'
                            }`}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : config.pricing && (
              <div className="bg-white rounded-xl p-6 md:p-8 shadow-md mb-4 inline-block max-w-lg">
                <p className="font-semibold text-[#153254] mb-3">Tarif</p>
                <div className="text-base md:text-lg text-gray-700 space-y-1">
                  {config.pricing.split('\n').map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              </div>
            )}
            
            {config.pricingNote && (
              <p className="text-sm text-gray-500 italic mb-6 max-w-xl mx-auto">
                * {config.pricingNote}
              </p>
            )}
            
            <p className="text-gray-600 mb-8 text-lg">
              Obtenez votre devis personnalisé en quelques clics
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={`/devis-premium?service=${serviceId}`}>
                <Button size="lg" className="bg-[#D4AF37] hover:bg-[#c9a030] text-white font-semibold px-10 py-6 text-lg shadow-md">
                  Demander un devis gratuit
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:0981543842">
                <Button size="lg" variant="outline" className="border-2 border-[#153254] text-[#153254] hover:bg-[#153254] hover:text-white font-semibold px-10 py-6 text-lg">
                  <Phone className="mr-2 h-5 w-5" />
                  09 81 54 38 42
                </Button>
              </a>
            </div>
            
            {/* Bouton voir tous les tarifs */}
            <div className="mt-8">
              <Link to="/tarifs">
                <button className="text-[#153254] font-medium underline underline-offset-4 hover:text-[#D4AF37] transition-colors">
                  Voir tous les tarifs →
                </button>
              </Link>
            </div>
            
            {config.hasTaxCredit && (
              <p className="text-gray-500 text-sm mt-8">
                Ce service est éligible au crédit d'impôt 50% avec avance immédiate
              </p>
            )}
          </div>
        </div>
      </section>
      
      {/* Floating Back Button - Amélioré */}
      <FloatingBackButton />
      
      <Footer />
    </div>
  );
};
